import { useState, useEffect, useReducer, useRef } from "react";

// ── Fonts & Global Styles ──────────────────────────────────────────────────
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,600&family=DM+Sans:wght@300;400;500;600&family=Playfair+Display:ital,wght@0,700;1,700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --olive:   #495E57;
      --lemon:   #F4CE14;
      --salmon:  #EE9972;
      --peach:   #FBDABB;
      --cream:   #FEFAE0;
      --charcoal:#2C2C2C;
      --mist:    #EDEFEE;
      --rust:    #C05D3A;
    }

    html { scroll-behavior: smooth; }

    body {
      font-family: 'DM Sans', sans-serif;
      background: var(--cream);
      color: var(--charcoal);
      overflow-x: hidden;
    }

    h1, h2, h3 {
      font-family: 'Cormorant Garamond', serif;
      line-height: 1.1;
    }

    /* ── Nav ── */
    .nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
      padding: 0 5%;
      display: flex; align-items: center; justify-content: space-between;
      height: 72px;
      transition: background 0.4s, box-shadow 0.4s;
    }
    .nav.scrolled {
      background: rgba(254,250,224,0.97);
      box-shadow: 0 1px 0 rgba(0,0,0,0.08);
      backdrop-filter: blur(10px);
    }
    .nav-logo {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.6rem; font-weight: 700;
      color: var(--olive); cursor: pointer;
      letter-spacing: -0.5px;
    }
    .nav-logo span { color: var(--lemon); }
    .nav-links {
      display: flex; gap: 2.5rem; list-style: none;
      font-size: 0.85rem; font-weight: 500; letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .nav-links a {
      color: var(--charcoal); text-decoration: none;
      position: relative; cursor: pointer;
      transition: color 0.2s;
    }
    .nav-links a::after {
      content: ''; position: absolute; bottom: -3px; left: 0;
      width: 0; height: 1.5px; background: var(--lemon);
      transition: width 0.3s;
    }
    .nav-links a:hover { color: var(--olive); }
    .nav-links a:hover::after { width: 100%; }
    .nav-reserve {
      background: var(--olive); color: var(--cream);
      border: none; padding: 10px 24px; border-radius: 2px;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.82rem; font-weight: 600;
      letter-spacing: 0.1em; text-transform: uppercase;
      cursor: pointer; transition: background 0.2s, transform 0.15s;
    }
    .nav-reserve:hover { background: var(--rust); transform: translateY(-1px); }
    .hamburger { display: none; flex-direction: column; gap: 5px; cursor: pointer; }
    .hamburger span { display: block; width: 24px; height: 2px; background: var(--charcoal); transition: all 0.3s; }
    @media (max-width: 768px) {
      .nav-links, .nav-reserve { display: none; }
      .hamburger { display: flex; }
    }

    /* ── Hero ── */
    .hero {
      min-height: 100vh;
      background: var(--olive);
      display: grid; grid-template-columns: 1fr 1fr;
      position: relative; overflow: hidden;
      padding-top: 72px;
    }
    .hero::before {
      content: '';
      position: absolute; inset: 0;
      background: radial-gradient(ellipse at 20% 80%, rgba(244,206,20,0.08) 0%, transparent 60%),
                  radial-gradient(ellipse at 80% 20%, rgba(238,153,114,0.06) 0%, transparent 60%);
      pointer-events: none;
    }
    .hero-left {
      display: flex; flex-direction: column;
      justify-content: center;
      padding: 80px 5% 80px 8%;
      position: relative; z-index: 2;
    }
    .hero-eyebrow {
      font-size: 0.8rem; font-weight: 600;
      letter-spacing: 0.25em; text-transform: uppercase;
      color: var(--lemon); margin-bottom: 1.5rem;
      display: flex; align-items: center; gap: 12px;
    }
    .hero-eyebrow::before {
      content: ''; display: block;
      width: 32px; height: 1px; background: var(--lemon);
    }
    .hero-h1 {
      font-size: clamp(4rem, 8vw, 7rem);
      color: var(--cream); font-weight: 300;
      letter-spacing: -2px; margin-bottom: 0.5rem;
      line-height: 1;
    }
    .hero-h1 em { color: var(--lemon); font-style: italic; }
    .hero-city {
      font-family: 'DM Sans', sans-serif;
      font-size: clamp(1rem, 2vw, 1.3rem);
      color: var(--peach); letter-spacing: 0.15em;
      text-transform: uppercase; margin-bottom: 2.5rem;
    }
    .hero-desc {
      color: rgba(254,250,224,0.75);
      font-size: 1.05rem; line-height: 1.75;
      max-width: 440px; margin-bottom: 3rem;
      font-weight: 300;
    }
    .hero-actions { display: flex; gap: 1rem; flex-wrap: wrap; }
    .btn-primary {
      background: var(--lemon); color: var(--charcoal);
      border: none; padding: 16px 36px; border-radius: 2px;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.9rem; font-weight: 600;
      letter-spacing: 0.08em; text-transform: uppercase;
      cursor: pointer; transition: all 0.25s;
    }
    .btn-primary:hover { background: #e6be00; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(244,206,20,0.35); }
    .btn-ghost {
      background: transparent; color: var(--cream);
      border: 1.5px solid rgba(254,250,224,0.35);
      padding: 16px 36px; border-radius: 2px;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.9rem; font-weight: 500;
      letter-spacing: 0.08em; text-transform: uppercase;
      cursor: pointer; transition: all 0.25s;
    }
    .btn-ghost:hover { border-color: var(--cream); background: rgba(254,250,224,0.08); }
    .hero-stats {
      display: flex; gap: 3rem; margin-top: 3.5rem;
      padding-top: 3rem; border-top: 1px solid rgba(254,250,224,0.12);
    }
    .stat-num {
      font-family: 'Cormorant Garamond', serif;
      font-size: 2.4rem; font-weight: 600; color: var(--lemon);
      line-height: 1;
    }
    .stat-label {
      font-size: 0.78rem; letter-spacing: 0.12em; text-transform: uppercase;
      color: rgba(254,250,224,0.5); margin-top: 4px;
    }
    .hero-right {
      position: relative; overflow: hidden;
      display: flex; align-items: center; justify-content: center;
    }
    .hero-img-wrap {
      position: absolute; inset: 72px 0 0 0;
    }
    .hero-img-wrap img {
      width: 100%; height: 100%; object-fit: cover;
      filter: brightness(0.8) saturate(1.1);
    }
    .hero-img-overlay {
      position: absolute; inset: 0;
      background: linear-gradient(135deg, rgba(73,94,87,0.6) 0%, transparent 60%);
    }
    .hero-badge {
      position: absolute; bottom: 40px; right: 6%;
      background: var(--lemon); color: var(--charcoal);
      width: 130px; height: 130px; border-radius: 50%;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center; text-align: center;
      font-family: 'Cormorant Garamond', serif;
      animation: spin-slow 20s linear infinite;
      cursor: default;
    }
    .hero-badge-inner { font-size: 1.8rem; font-weight: 700; line-height: 1; }
    .hero-badge-sub { font-size: 0.65rem; font-weight: 600; letter-spacing: 0.15em; text-transform: uppercase; margin-top: 2px; }
    @keyframes spin-slow { to { transform: rotate(360deg); } }
    @media (max-width: 768px) {
      .hero { grid-template-columns: 1fr; }
      .hero-right { display: none; }
      .hero-left { padding: 60px 8%; }
    }

    /* ── Section Headings ── */
    .section-tag {
      font-size: 0.75rem; font-weight: 600;
      letter-spacing: 0.25em; text-transform: uppercase;
      color: var(--rust); display: flex; align-items: center; gap: 10px;
      margin-bottom: 1rem;
    }
    .section-tag::before {
      content: ''; width: 24px; height: 1.5px; background: var(--rust);
    }
    .section-h2 {
      font-size: clamp(2.4rem, 4vw, 3.5rem);
      font-weight: 300; color: var(--charcoal);
      letter-spacing: -1px; margin-bottom: 1.5rem;
    }
    .section-h2 em { color: var(--olive); font-style: italic; }

    /* ── Specials / Menu ── */
    .specials {
      padding: 100px 8%;
      background: var(--cream);
    }
    .specials-head {
      display: flex; justify-content: space-between; align-items: flex-end;
      margin-bottom: 3.5rem; flex-wrap: wrap; gap: 1rem;
    }
    .btn-outline {
      background: transparent; color: var(--olive);
      border: 1.5px solid var(--olive);
      padding: 12px 28px; border-radius: 2px;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.82rem; font-weight: 600;
      letter-spacing: 0.1em; text-transform: uppercase;
      cursor: pointer; transition: all 0.25s;
    }
    .btn-outline:hover { background: var(--olive); color: var(--cream); }
    .cards-grid {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
    }
    @media (max-width: 900px) { .cards-grid { grid-template-columns: 1fr; } }
    .card {
      background: #fff; border-radius: 4px;
      overflow: hidden; position: relative;
      transition: transform 0.3s, box-shadow 0.3s;
      cursor: pointer;
    }
    .card:hover { transform: translateY(-6px); box-shadow: 0 20px 48px rgba(73,94,87,0.15); }
    .card-img-wrap { position: relative; height: 220px; overflow: hidden; }
    .card-img-wrap img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s; }
    .card:hover .card-img-wrap img { transform: scale(1.06); }
    .card-badge {
      position: absolute; top: 14px; left: 14px;
      background: var(--lemon); color: var(--charcoal);
      padding: 4px 12px; border-radius: 1px;
      font-size: 0.72rem; font-weight: 700;
      letter-spacing: 0.1em; text-transform: uppercase;
    }
    .card-body { padding: 24px; }
    .card-head {
      display: flex; justify-content: space-between; align-items: flex-start;
      margin-bottom: 12px;
    }
    .card-title {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.5rem; font-weight: 600; color: var(--charcoal);
    }
    .card-price {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.4rem; font-weight: 600; color: var(--rust);
    }
    .card-desc { font-size: 0.88rem; color: #666; line-height: 1.65; margin-bottom: 20px; }
    .card-cta {
      display: flex; align-items: center; gap: 8px;
      font-size: 0.82rem; font-weight: 600;
      letter-spacing: 0.1em; text-transform: uppercase;
      color: var(--olive); cursor: pointer;
      border: none; background: none;
      transition: gap 0.2s;
    }
    .card-cta:hover { gap: 14px; }
    .card-cta::after { content: '→'; font-size: 1rem; }

    /* ── About ── */
    .about {
      background: var(--olive);
      display: grid; grid-template-columns: 1fr 1fr;
      min-height: 600px;
    }
    .about-left {
      position: relative; overflow: hidden;
    }
    .about-imgs {
      position: relative; height: 100%; min-height: 500px;
    }
    .about-img1 {
      position: absolute; top: 40px; left: 10%; width: 65%; height: 75%;
      border-radius: 4px; overflow: hidden;
    }
    .about-img2 {
      position: absolute; bottom: 30px; right: 5%; width: 50%; height: 55%;
      border-radius: 4px; overflow: hidden;
      border: 6px solid var(--olive);
    }
    .about-img1 img, .about-img2 img { width: 100%; height: 100%; object-fit: cover; }
    .about-right {
      padding: 80px 8% 80px 6%;
      display: flex; flex-direction: column; justify-content: center;
    }
    .about-right .section-tag { color: var(--peach); }
    .about-right .section-tag::before { background: var(--peach); }
    .about-h2 {
      font-size: clamp(2.2rem, 3.5vw, 3rem);
      color: var(--cream); font-weight: 300;
      letter-spacing: -0.5px; margin-bottom: 1.5rem;
    }
    .about-h2 em { color: var(--lemon); font-style: italic; }
    .about-p {
      color: rgba(254,250,224,0.72);
      font-size: 1rem; line-height: 1.8; margin-bottom: 1.2rem;
    }
    @media (max-width: 768px) {
      .about { grid-template-columns: 1fr; }
      .about-left { min-height: 350px; display: block; }
      .about-imgs { min-height: 350px; }
    }

    /* ── Testimonials ── */
    .testimonials {
      padding: 100px 8%;
      background: var(--mist);
    }
    .testi-grid {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem; margin-top: 3.5rem;
    }
    @media (max-width: 900px) { .testi-grid { grid-template-columns: 1fr; } }
    .testi-card {
      background: #fff; padding: 32px;
      border-radius: 4px; border-left: 4px solid var(--lemon);
      transition: transform 0.3s;
    }
    .testi-card:hover { transform: translateY(-4px); }
    .testi-stars { color: var(--lemon); font-size: 1rem; margin-bottom: 16px; letter-spacing: 2px; }
    .testi-quote {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.15rem; font-style: italic; line-height: 1.65;
      color: var(--charcoal); margin-bottom: 20px;
    }
    .testi-author { font-size: 0.82rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--olive); }

    /* ── CTA Banner ── */
    .cta-banner {
      background: var(--lemon);
      padding: 80px 8%;
      display: flex; align-items: center; justify-content: space-between;
      flex-wrap: wrap; gap: 2rem;
    }
    .cta-text h2 {
      font-size: clamp(2rem, 3.5vw, 2.8rem);
      color: var(--charcoal); font-weight: 400;
    }
    .cta-text p { color: rgba(44,44,44,0.65); margin-top: 8px; font-size: 1rem; }
    .btn-dark {
      background: var(--charcoal); color: var(--cream);
      border: none; padding: 16px 40px; border-radius: 2px;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.9rem; font-weight: 600;
      letter-spacing: 0.1em; text-transform: uppercase;
      cursor: pointer; transition: all 0.25s; white-space: nowrap;
    }
    .btn-dark:hover { background: var(--olive); transform: translateY(-2px); }

    /* ── Footer ── */
    footer {
      background: var(--charcoal);
      padding: 80px 8% 40px;
      color: rgba(254,250,224,0.6);
    }
    .footer-grid {
      display: grid; grid-template-columns: 2fr 1fr 1fr 1fr;
      gap: 3rem; margin-bottom: 60px;
    }
    @media (max-width: 900px) { .footer-grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 500px) { .footer-grid { grid-template-columns: 1fr; } }
    .footer-brand-name {
      font-family: 'Cormorant Garamond', serif;
      font-size: 2rem; font-weight: 700; color: var(--cream);
      margin-bottom: 12px;
    }
    .footer-brand-name span { color: var(--lemon); }
    .footer-brand-desc { font-size: 0.9rem; line-height: 1.7; max-width: 280px; }
    .footer-col h4 {
      font-family: 'DM Sans', sans-serif;
      font-size: 0.75rem; font-weight: 700;
      letter-spacing: 0.2em; text-transform: uppercase;
      color: var(--cream); margin-bottom: 20px;
    }
    .footer-col ul { list-style: none; }
    .footer-col ul li { margin-bottom: 10px; }
    .footer-col ul li a {
      color: rgba(254,250,224,0.55); text-decoration: none; font-size: 0.9rem;
      transition: color 0.2s; cursor: pointer;
    }
    .footer-col ul li a:hover { color: var(--lemon); }
    .footer-bottom {
      padding-top: 30px; border-top: 1px solid rgba(254,250,224,0.08);
      display: flex; justify-content: space-between; align-items: center;
      flex-wrap: wrap; gap: 1rem;
      font-size: 0.8rem;
    }

    /* ── Booking Page ── */
    .booking-page {
      min-height: 100vh; padding-top: 72px;
      background: var(--cream);
      display: grid; grid-template-columns: 1fr 1fr;
    }
    @media (max-width: 768px) { .booking-page { grid-template-columns: 1fr; } }
    .booking-left {
      background: var(--olive);
      padding: 80px 6% 80px 8%;
      display: flex; flex-direction: column; justify-content: center;
      position: relative; overflow: hidden;
    }
    .booking-left::after {
      content: 'LL'; 
      position: absolute; bottom: -20px; right: -20px;
      font-family: 'Cormorant Garamond', serif;
      font-size: 14rem; font-weight: 700;
      color: rgba(255,255,255,0.04);
      line-height: 1; pointer-events: none;
    }
    .booking-left h1 {
      font-size: clamp(2.5rem, 4vw, 3.5rem);
      color: var(--cream); font-weight: 300;
      letter-spacing: -1px; margin-bottom: 1.5rem;
    }
    .booking-left h1 em { color: var(--lemon); font-style: italic; }
    .booking-left p {
      color: rgba(254,250,224,0.65);
      font-size: 1rem; line-height: 1.75; margin-bottom: 2.5rem;
    }
    .booking-info { display: flex; flex-direction: column; gap: 16px; }
    .booking-info-item {
      display: flex; gap: 14px; align-items: flex-start;
    }
    .bi-icon {
      width: 40px; height: 40px; border-radius: 50%;
      background: rgba(244,206,20,0.15);
      display: flex; align-items: center; justify-content: center;
      font-size: 1.1rem; flex-shrink: 0; color: var(--lemon);
    }
    .bi-text { font-size: 0.88rem; color: rgba(254,250,224,0.65); line-height: 1.5; }
    .bi-label { color: var(--cream); font-weight: 600; font-size: 0.85rem; margin-bottom: 2px; }
    .booking-right {
      padding: 80px 8% 80px 6%;
      display: flex; flex-direction: column; justify-content: center;
    }
    .booking-right h2 {
      font-size: 2rem; color: var(--charcoal); margin-bottom: 0.5rem;
    }
    .booking-right > p { color: #888; font-size: 0.9rem; margin-bottom: 2.5rem; }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1.2rem; }
    .form-full { grid-column: span 2; }
    @media (max-width: 500px) { .form-grid { grid-template-columns: 1fr; } .form-full { grid-column: span 1; } }
    .form-field { display: flex; flex-direction: column; gap: 6px; }
    .form-label {
      font-size: 0.75rem; font-weight: 700;
      letter-spacing: 0.15em; text-transform: uppercase; color: var(--olive);
    }
    .form-input, .form-select {
      padding: 13px 16px;
      border: 1.5px solid #ddd; border-radius: 2px;
      font-family: 'DM Sans', sans-serif; font-size: 0.95rem;
      color: var(--charcoal); background: #fff;
      transition: border-color 0.2s, box-shadow 0.2s;
      outline: none;
      appearance: none;
    }
    .form-input:focus, .form-select:focus {
      border-color: var(--olive);
      box-shadow: 0 0 0 3px rgba(73,94,87,0.1);
    }
    .form-select-wrap { position: relative; }
    .form-select-wrap::after {
      content: '▾';
      position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
      color: var(--olive); pointer-events: none; font-size: 1rem;
    }
    .btn-submit {
      background: var(--olive); color: var(--cream);
      border: none; padding: 16px;
      border-radius: 2px; font-family: 'DM Sans', sans-serif;
      font-size: 0.9rem; font-weight: 700;
      letter-spacing: 0.12em; text-transform: uppercase;
      cursor: pointer; transition: all 0.25s; width: 100%;
      margin-top: 0.5rem;
    }
    .btn-submit:hover { background: var(--rust); transform: translateY(-2px); box-shadow: 0 8px 24px rgba(192,93,58,0.3); }

    /* ── Confirmation Page ── */
    .confirm-page {
      min-height: 100vh; padding-top: 72px;
      background: var(--olive);
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      text-align: center; padding: 100px 8%;
    }
    .confirm-icon {
      width: 100px; height: 100px; border-radius: 50%;
      background: var(--lemon);
      display: flex; align-items: center; justify-content: center;
      font-size: 2.5rem; margin: 0 auto 2rem;
      animation: pop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }
    @keyframes pop { from { transform: scale(0); } to { transform: scale(1); } }
    .confirm-page h1 {
      font-size: clamp(2.5rem, 5vw, 4rem);
      color: var(--cream); font-weight: 300;
      letter-spacing: -1px; margin-bottom: 1rem;
    }
    .confirm-page h1 em { color: var(--lemon); font-style: italic; }
    .confirm-page > p {
      color: rgba(254,250,224,0.65); font-size: 1.1rem;
      line-height: 1.7; max-width: 500px; margin: 0 auto 2.5rem;
    }
    .confirm-details {
      background: rgba(254,250,224,0.08);
      border: 1px solid rgba(254,250,224,0.15);
      border-radius: 4px; padding: 28px 40px;
      display: grid; grid-template-columns: 1fr 1fr;
      gap: 1.5rem 3rem; text-align: left;
      margin-bottom: 2.5rem; max-width: 500px; width: 100%;
    }
    .cd-label {
      font-size: 0.72rem; font-weight: 700;
      letter-spacing: 0.18em; text-transform: uppercase; color: var(--peach);
      margin-bottom: 4px;
    }
    .cd-val {
      font-family: 'Cormorant Garamond', serif;
      font-size: 1.2rem; color: var(--cream);
    }
    .btn-home {
      background: var(--lemon); color: var(--charcoal);
      border: none; padding: 16px 40px; border-radius: 2px;
      font-family: 'DM Sans', sans-serif;
      font-size: 0.9rem; font-weight: 700;
      letter-spacing: 0.1em; text-transform: uppercase;
      cursor: pointer; transition: all 0.25s;
    }
    .btn-home:hover { background: #e6be00; transform: translateY(-2px); }

    /* ── Mobile Menu Overlay ── */
    .mobile-nav {
      display: none; position: fixed;
      top: 72px; left: 0; right: 0;
      background: var(--cream);
      z-index: 999; padding: 2rem 8%;
      flex-direction: column; gap: 1.5rem;
      box-shadow: 0 8px 32px rgba(0,0,0,0.1);
    }
    .mobile-nav.open { display: flex; }
    .mobile-nav a {
      font-size: 1.1rem; color: var(--charcoal);
      text-decoration: none; font-weight: 500;
      cursor: pointer;
    }

    /* ── Page transitions ── */
    .page-enter {
      animation: fadeUp 0.5s ease forwards;
    }
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `}</style>
);

// ── Data ──────────────────────────────────────────────────────────────────
const RECIPES = [
  {
    id: 1, title: "Greek Salad", price: "$12.99", badge: "Vegetarian",
    image: "https://i.postimg.cc/nz9bHwyL/greek-salad.jpg",
    description: "Crispy romaine, Kalamata olives, ripe tomatoes, cucumber, and authentic Chicago-style feta, finished with house-herb vinaigrette.",
  },
  {
    id: 2, title: "Bruschetta", price: "$7.99", badge: "Chef's Pick",
    image: "https://i.ibb.co/317GqZv/Bruchetta.png",
    description: "Charred sourdough smeared with roasted garlic, peak-season tomatoes, fresh basil, and a drizzle of first-press Sicilian olive oil.",
  },
  {
    id: 3, title: "Grilled Fish", price: "$20.99", badge: "Signature",
    image: "https://i.postimg.cc/XqRq9hfp/grilled-fish.jpg",
    description: "Fresh market fillet, perfectly seasoned and flame-grilled, with lemon-infused olive oil, capers, and seasonal herbs.",
  },
];

const TESTIMONIALS = [
  { name: "Maria C.", stars: 5, quote: "An absolute gem. The food transported me straight to the Amalfi Coast — every bite was perfection. We've become regulars." },
  { name: "James T.", stars: 5, quote: "The grilled fish is unlike anything I've had in Chicago. Elegant, unfussy, and deeply flavorful. Highly recommend." },
  { name: "Aisha R.", stars: 5, quote: "From the ambiance to the bruschetta, everything was 10/10. Little Lemon has raised the bar for Mediterranean dining in the city." },
];

// ── Helpers ───────────────────────────────────────────────────────────────
const seedRandom = (seed) => {
  let s = seed % (2 ** 35 - 31);
  return () => (s = (s * 185852) % (2 ** 35 - 31)) / (2 ** 35 - 31);
};
const fetchTimes = (date) => {
  const r = seedRandom(new Date(date).getDate() || new Date().getDate());
  const slots = [];
  for (let h = 17; h <= 23; h++) {
    if (r() < 0.5) slots.push(`${h}:00`);
    if (r() > 0.5) slots.push(`${h}:30`);
  }
  return slots.length ? slots : ["18:00", "19:00", "20:00"];
};
const reducer = (_, date) => fetchTimes(date);

// ── Components ────────────────────────────────────────────────────────────

function Navbar({ page, setPage }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  const nav = (p) => { setPage(p); setMobileOpen(false); window.scrollTo(0, 0); };
  return (
    <>
      <nav className={`nav ${scrolled || page !== "home" ? "scrolled" : ""}`}>
        <div className="nav-logo" onClick={() => nav("home")}>
          Little <span>Lemon</span>
        </div>
        <ul className="nav-links">
          <li><a onClick={() => nav("home")}>Home</a></li>
          <li><a onClick={() => nav("home")}>Menu</a></li>
          <li><a onClick={() => nav("home")}>About</a></li>
          <li><a onClick={() => nav("home")}>Contact</a></li>
        </ul>
        <button className="nav-reserve" onClick={() => nav("booking")}>Reserve a Table</button>
        <div className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>
          <span /><span /><span />
        </div>
      </nav>
      <div className={`mobile-nav ${mobileOpen ? "open" : ""}`}>
        <a onClick={() => nav("home")}>Home</a>
        <a onClick={() => nav("home")}>Menu</a>
        <a onClick={() => nav("home")}>About</a>
        <button className="btn-primary" style={{width:"100%"}} onClick={() => nav("booking")}>Reserve a Table</button>
      </div>
    </>
  );
}

function Hero({ setPage }) {
  return (
    <div className="hero page-enter">
      <div className="hero-left">
        <div className="hero-eyebrow">Mediterranean Restaurant</div>
        <h1 className="hero-h1">Little<br/><em>Lemon</em></h1>
        <p className="hero-city">Chicago, Illinois</p>
        <p className="hero-desc">A family-owned corner of the Mediterranean, where traditional recipes meet the vibrant spirit of modern Chicago cuisine.</p>
        <div className="hero-actions">
          <button className="btn-primary" onClick={() => setPage("booking")}>Reserve a Table</button>
          <button className="btn-ghost">Explore Menu</button>
        </div>
        <div className="hero-stats">
          <div><div className="stat-num">12+</div><div className="stat-label">Years of craft</div></div>
          <div><div className="stat-num">4.9★</div><div className="stat-label">Average rating</div></div>
          <div><div className="stat-num">40k</div><div className="stat-label">Happy guests</div></div>
        </div>
      </div>
      <div className="hero-right">
        <div className="hero-img-wrap">
          <img src="https://i.postimg.cc/g0VcPd1C/restaurantfood.jpg" alt="Little Lemon cuisine" />
          <div className="hero-img-overlay" />
        </div>
        <div className="hero-badge">
          <div className="hero-badge-inner">Est.<br/>2012</div>
          <div className="hero-badge-sub">Chicago</div>
        </div>
      </div>
    </div>
  );
}

function Specials({ setPage }) {
  return (
    <section className="specials">
      <div className="specials-head">
        <div>
          <div className="section-tag">This Week</div>
          <h2 className="section-h2">Chef's <em>Specials</em></h2>
        </div>
        <button className="btn-outline" onClick={() => setPage("booking")}>View Full Menu</button>
      </div>
      <div className="cards-grid">
        {RECIPES.map(r => (
          <div className="card" key={r.id}>
            <div className="card-img-wrap">
              <img src={r.image} alt={r.title} />
              <span className="card-badge">{r.badge}</span>
            </div>
            <div className="card-body">
              <div className="card-head">
                <div className="card-title">{r.title}</div>
                <div className="card-price">{r.price}</div>
              </div>
              <p className="card-desc">{r.description}</p>
              <button className="card-cta">Order Now</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="about">
      <div className="about-left">
        <div className="about-imgs">
          <div className="about-img1">
            <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80" alt="Restaurant interior" />
          </div>
          <div className="about-img2">
            <img src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80" alt="Mediterranean food" />
          </div>
        </div>
      </div>
      <div className="about-right">
        <div className="section-tag">Our Story</div>
        <h2 className="about-h2">A Taste of the<br/><em>Mediterranean</em></h2>
        <p className="about-p">Little Lemon was born from a simple dream — to bring the warmth, color, and soul of Mediterranean cooking to the heart of Chicago. Founded by the Kowalski family in 2012, we've spent over a decade perfecting recipes passed down through generations.</p>
        <p className="about-p">Every dish is made with ingredients sourced from local farmers and artisan importers, honoring the land while celebrating the traditions of Greece, Italy, and the Levant.</p>
        <button className="btn-primary" style={{marginTop:"1rem", alignSelf:"flex-start"}}>Learn More About Us</button>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="testimonials">
      <div className="section-tag">Guest Voices</div>
      <h2 className="section-h2">What Our Guests <em>Say</em></h2>
      <div className="testi-grid">
        {TESTIMONIALS.map((t, i) => (
          <div className="testi-card" key={i}>
            <div className="testi-stars">{"★".repeat(t.stars)}</div>
            <p className="testi-quote">"{t.quote}"</p>
            <div className="testi-author">— {t.name}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CTABanner({ setPage }) {
  return (
    <div className="cta-banner">
      <div className="cta-text">
        <h2>Ready for an unforgettable<br/>dining experience?</h2>
        <p>Tables fill quickly — reserve yours today.</p>
      </div>
      <button className="btn-dark" onClick={() => setPage("booking")}>Book Your Table →</button>
    </div>
  );
}

function Footer({ setPage }) {
  return (
    <footer>
      <div className="footer-grid">
        <div>
          <div className="footer-brand-name">Little <span>Lemon</span></div>
          <p className="footer-brand-desc">A family-owned Mediterranean restaurant on South Michigan Ave, bringing traditional recipes with a modern twist since 2012.</p>
        </div>
        <div className="footer-col">
          <h4>Navigate</h4>
          <ul>
            <li><a onClick={() => setPage("home")}>Home</a></li>
            <li><a>About</a></li>
            <li><a>Menu</a></li>
            <li><a onClick={() => setPage("booking")}>Reservations</a></li>
            <li><a>Order Online</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Contact</h4>
          <ul>
            <li><a>12 S Michigan Ave</a></li>
            <li><a>Chicago, IL 60603</a></li>
            <li><a>+1 312-456-7891</a></li>
            <li><a>little@lemon.com</a></li>
          </ul>
        </div>
        <div className="footer-col">
          <h4>Social</h4>
          <ul>
            <li><a>Instagram</a></li>
            <li><a>Facebook</a></li>
            <li><a>Twitter</a></li>
            <li><a>TripAdvisor</a></li>
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <span>© 2026 Little Lemon. All rights reserved.</span>
        <span>Privacy Policy · Terms of Service</span>
      </div>
    </footer>
  );
}

function HomePage({ setPage }) {
  return (
    <div>
      <Hero setPage={setPage} />
      <Specials setPage={setPage} />
      <About />
      <Testimonials />
      <CTABanner setPage={setPage} />
      <Footer setPage={setPage} />
    </div>
  );
}

function BookingPage({ setPage }) {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState(2);
  const [occasion, setOccasion] = useState("None");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [times, dispatch] = useReducer(reducer, fetchTimes(today));

  const handleDate = (v) => { setDate(v); dispatch(v); setTime(""); };

  const handleSubmit = () => {
    if (!date || !time || !guests || !name || !email) {
      alert("Please fill in all required fields.");
      return;
    }
    setPage("confirmed", { date, time, guests, occasion, name });
  };

  return (
    <div className="booking-page page-enter">
      <div className="booking-left">
        <div className="section-tag" style={{color:"var(--peach)"}}>
          <span style={{display:"block",width:24,height:1.5,background:"var(--peach)"}}></span>
          Make a Reservation
        </div>
        <h1>Reserve Your<br/><em>Table</em></h1>
        <p>Join us for an unforgettable Mediterranean dining experience in the heart of Chicago. We look forward to welcoming you.</p>
        <div className="booking-info">
          <div className="booking-info-item">
            <div className="bi-icon">🕐</div>
            <div className="bi-text">
              <div className="bi-label">Hours</div>
              Mon–Fri: 11am – 10pm<br/>Sat–Sun: 10am – 11pm
            </div>
          </div>
          <div className="booking-info-item">
            <div className="bi-icon">📍</div>
            <div className="bi-text">
              <div className="bi-label">Location</div>
              12 S Michigan Ave, Chicago, IL 60603
            </div>
          </div>
          <div className="booking-info-item">
            <div className="bi-icon">📞</div>
            <div className="bi-text">
              <div className="bi-label">Phone</div>
              +1 312-456-7891
            </div>
          </div>
        </div>
      </div>

      <div className="booking-right">
        <h2>Booking Details</h2>
        <p>Complete the form below and we'll confirm within 2 hours.</p>
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">Full Name *</label>
            <input className="form-input" type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="form-label">Email *</label>
            <input className="form-input" type="email" placeholder="you@email.com" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="form-label">Date *</label>
            <input className="form-input" type="date" min={today} value={date} onChange={e => handleDate(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="form-label">Time *</label>
            <div className="form-select-wrap">
              <select className="form-select" value={time} onChange={e => setTime(e.target.value)}>
                <option value="">Select a time</option>
                {times.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-field">
            <label className="form-label">Guests *</label>
            <input className="form-input" type="number" min="1" max="20" value={guests} onChange={e => setGuests(e.target.value)} />
          </div>
          <div className="form-field">
            <label className="form-label">Occasion</label>
            <div className="form-select-wrap">
              <select className="form-select" value={occasion} onChange={e => setOccasion(e.target.value)}>
                <option>None</option>
                <option>Birthday</option>
                <option>Anniversary</option>
                <option>Business Dinner</option>
                <option>Date Night</option>
                <option>Other</option>
              </select>
            </div>
          </div>
          <div className="form-field form-full">
            <label className="form-label">Special Requests</label>
            <input className="form-input" type="text" placeholder="Allergies, seating preferences, etc." />
          </div>
          <div className="form-field form-full">
            <button className="btn-submit" onClick={handleSubmit}>Confirm Reservation →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfirmPage({ booking, setPage }) {
  return (
    <div className="confirm-page page-enter">
      <div className="confirm-icon">✓</div>
      <h1>Booking <em>Confirmed!</em></h1>
      <p>Thank you{booking?.name ? `, ${booking.name}` : ""}! Your table at Little Lemon has been reserved. You'll receive a confirmation email shortly.</p>
      {booking && (
        <div className="confirm-details">
          <div><div className="cd-label">Date</div><div className="cd-val">{booking.date}</div></div>
          <div><div className="cd-label">Time</div><div className="cd-val">{booking.time}</div></div>
          <div><div className="cd-label">Guests</div><div className="cd-val">{booking.guests}</div></div>
          <div><div className="cd-label">Occasion</div><div className="cd-val">{booking.occasion || "—"}</div></div>
        </div>
      )}
      <button className="btn-home" onClick={() => setPage("home")}>← Back to Home</button>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home");
  const [bookingData, setBookingData] = useState(null);

  const navigate = (p, data) => {
    setPage(p);
    if (data) setBookingData(data);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <GlobalStyle />
      <Navbar page={page} setPage={navigate} />
      {page === "home"      && <HomePage setPage={navigate} />}
      {page === "booking"   && <BookingPage setPage={navigate} />}
      {page === "confirmed" && <ConfirmPage booking={bookingData} setPage={navigate} />}
    </>
  );
}
